/**
 * Webtech HR Engine™ - My Skills Page (Employee Space)
 * Page dédiée aux compétences pour les employés connectés
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
  Brain,
  Target,
  TrendingUp,
  Award,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ChevronRight,
  Lightbulb,
  BookOpen,
  Zap,
  Star,
  AlertTriangle,
  ArrowRight,
  GraduationCap
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

export default function MySkillsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const companyId = user?.companyId;
  const userId = user?.id;
  
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [notLinked, setNotLinked] = useState(false);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [developmentPlans, setDevelopmentPlans] = useState([]);
  const [skillsMatrix, setSkillsMatrix] = useState([]);

  useEffect(() => {
    if (companyId && userId) {
      loadMyEmployee();
    }
  }, [companyId, userId]);

  const loadMyEmployee = async () => {
    try {
      setLoading(true);
      
      // First, get the employee linked to this user
      const empRes = await api.get(`/employee-accounts/my-employee?user_id=${userId}&company_id=${companyId}`);
      
      if (!empRes.data.found) {
        setNotLinked(true);
        setLoading(false);
        return;
      }
      
      setEmployee(empRes.data.employee);
      const employeeId = empRes.data.employee.id;
      
      // Load skills data
      await loadSkillsData(employeeId);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
      setNotLinked(true);
    } finally {
      setLoading(false);
    }
  };

  const loadSkillsData = async (employeeId) => {
    try {
      const [skillsRes, plansRes, radarRes] = await Promise.all([
        api.get(`/skills-intelligence/employee/${employeeId}?company_id=${companyId}`).catch(() => ({ data: {} })),
        api.get(`/skills-intelligence/upskilling-plans?company_id=${companyId}&employee_id=${employeeId}`).catch(() => ({ data: { plans: [] } })),
        api.get(`/skills-intelligence/employee/${employeeId}/radar?company_id=${companyId}`).catch(() => ({ data: {} }))
      ]);
      
      // Transform skills intelligence data to gap analysis format
      const skillProfile = skillsRes.data?.skillProfile;
      const radarData = radarRes.data;
      
      if (skillProfile) {
        // Build gap analysis from skill profile
        const gaps = (skillProfile.skills || []).map(s => ({
          skillId: s.skillId,
          skillName: s.skillName,
          currentLevel: s.currentLevel || 0,
          requiredLevel: s.requiredLevel || 3,
          gap: s.gap || 0,
          importance: s.importance || 'medium',
          status: s.currentLevel >= s.requiredLevel ? 'met' : 'gap'
        }));
        
        const readinessScore = Math.round(skillProfile.overallScore || 0);
        setGapAnalysis({
          hasJobProfile: true,
          jobProfile: { name: skillProfile.jobProfileName },
          gaps: gaps,
          readinessScore: readinessScore,
          summary: {
            totalSkills: skillProfile.skills?.length || 0,
            skillsMet: gaps.filter(g => g.status === 'met').length,
            skillsAbove: gaps.filter(g => g.currentLevel > g.requiredLevel).length,
            skillsWithGap: gaps.filter(g => g.status === 'gap').length,
            criticalGaps: skillProfile.criticalGaps || 0,
            readinessScore: readinessScore
          }
        });
      } else {
        setGapAnalysis({ hasJobProfile: false });
      }
      
      setDevelopmentPlans(plansRes.data.plans || []);
      // Transform skills to expected format
      const formattedSkills = (skillProfile?.skills || []).map(s => ({
        ...s,
        name: s.skillName || s.name,
        level: s.currentLevel || 0,
        score: s.currentLevel || 0,
        category: s.importance === 'critical' ? 'Critiques' : 'Autres'
      }));
      setSkillsMatrix(formattedSkills);
    } catch (error) {
      console.error('Error loading skills data:', error);
    }
  };

  const getReadinessColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-50';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getLevelColor = (level) => {
    if (level >= 4) return 'bg-green-500';
    if (level >= 3) return 'bg-blue-500';
    if (level >= 2) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getLevelLabel = (level) => {
    if (level >= 4) return 'Expert';
    if (level >= 3) return 'Avancé';
    if (level >= 2) return 'Intermédiaire';
    return 'Débutant';
  };

  if (loading) {
    return (
      <DashboardLayout title="Mes compétences">
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  // If user is not linked to an employee
  if (notLinked) {
    return (
      <DashboardLayout title="Mes compétences">
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Brain className="h-7 w-7 text-purple-500" />
              Mes compétences
            </h1>
            <p className="text-slate-500 mt-1">Visualisez et développez vos compétences</p>
          </div>

          <Card className="py-12 border-yellow-200 bg-yellow-50">
            <CardContent className="text-center">
              <AlertTriangle className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Compte non lié</h3>
              <p className="text-slate-600 max-w-md mx-auto mb-4">
                Votre compte utilisateur n&apos;est pas encore lié à votre fiche employé. 
                Contactez votre responsable RH pour activer votre accès.
              </p>
              <div className="bg-white rounded-lg p-4 max-w-sm mx-auto border">
                <p className="text-sm text-slate-500">Connecté en tant que :</p>
                <p className="font-medium text-slate-900">{user?.email}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const activePlans = developmentPlans.filter(p => p.status === 'active');
  const completedPlans = developmentPlans.filter(p => p.status === 'completed');
  const skillsByCategory = skillsMatrix.reduce((acc, skill) => {
    const cat = skill.category || 'Autres';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(skill);
    return acc;
  }, {});

  return (
    <DashboardLayout title="Mes compétences">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Brain className="h-7 w-7 text-purple-500" />
              Mes compétences
            </h1>
            <p className="text-slate-500 mt-1">Visualisez et développez vos compétences professionnelles</p>
          </div>
          
          {employee && (
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="py-3 px-4 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-medium">
                  {employee.firstName?.[0]}{employee.lastName?.[0]}
                </div>
                <div>
                  <p className="font-medium text-slate-900">{employee.firstName} {employee.lastName}</p>
                  <p className="text-sm text-purple-600">{employee.jobTitle || 'Employé'}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Readiness Score */}
        {gapAnalysis?.hasJobProfile && (
          <Card className={`border-2 ${getReadinessColor(gapAnalysis.readinessScore || 0).replace('text-', 'border-').replace('-600', '-200')}`}>
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${getReadinessColor(gapAnalysis.readinessScore || 0)}`}>
                    <Target className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Score de préparation au poste</p>
                    <p className={`text-3xl font-bold ${getReadinessColor(gapAnalysis.readinessScore || 0).split(' ')[0]}`}>
                      {gapAnalysis.readinessScore || 0}%
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Profil cible</p>
                  <p className="font-medium text-slate-900">{gapAnalysis.jobProfile?.name || employee?.jobTitle}</p>
                </div>
              </div>
              
              <Progress 
                value={gapAnalysis.readinessScore || 0} 
                className="h-3 mt-4"
              />
              
              <p className="text-sm text-slate-600 mt-3">
                {gapAnalysis.readinessScore >= 80 
                  ? "Excellent ! Vous maîtrisez les compétences clés de votre poste." 
                  : gapAnalysis.readinessScore >= 60 
                    ? "Bon niveau ! Quelques compétences peuvent encore être renforcées."
                    : "Des opportunités de développement ont été identifiées."}
              </p>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Skills Matrix */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Mes compétences évaluées
              </CardTitle>
              <CardDescription>
                {skillsMatrix.length} compétence{skillsMatrix.length > 1 ? 's' : ''} dans votre profil
              </CardDescription>
            </CardHeader>
            <CardContent>
              {skillsMatrix.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Brain className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>Aucune compétence évaluée pour le moment</p>
                  <p className="text-sm mt-1">Les évaluations apparaîtront ici</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(skillsByCategory).map(([category, skills]) => (
                    <div key={category}>
                      <p className="text-xs font-medium text-slate-400 uppercase mb-2">{category}</p>
                      <div className="space-y-2">
                        {skills.map((skill, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-8 rounded-full ${getLevelColor(skill.level || skill.score || 0)}`} />
                              <div>
                                <p className="font-medium text-slate-900">{skill.name}</p>
                                <p className="text-xs text-slate-500">{getLevelLabel(skill.level || skill.score || 0)}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex gap-1">
                                {[1, 2, 3, 4, 5].map((level) => (
                                  <div 
                                    key={level}
                                    className={`w-2 h-2 rounded-full ${
                                      level <= (skill.level || skill.score || 0) 
                                        ? getLevelColor(skill.level || skill.score || 0)
                                        : 'bg-slate-200'
                                    }`}
                                  />
                                ))}
                              </div>
                              <span className="text-sm font-medium text-slate-700 w-8 text-right">
                                {skill.level || skill.score || 0}/5
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Skills Gaps */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-500" />
                Axes de progression
              </CardTitle>
              <CardDescription>
                Compétences à développer pour atteindre vos objectifs
              </CardDescription>
            </CardHeader>
            <CardContent>
              {!gapAnalysis?.hasJobProfile ? (
                <div className="text-center py-8 text-slate-500">
                  <Target className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p>Aucun profil de poste défini</p>
                  <p className="text-sm mt-1">L'analyse des écarts sera disponible une fois votre profil configuré</p>
                </div>
              ) : gapAnalysis?.gaps?.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle2 className="w-12 h-12 mx-auto text-green-500 mb-3" />
                  <p className="text-green-700 font-medium">Félicitations !</p>
                  <p className="text-sm text-slate-500 mt-1">Vous maîtrisez toutes les compétences requises</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {(gapAnalysis?.gaps || []).slice(0, 5).map((gap, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-slate-900">{gap.skillName}</p>
                        <Badge variant="outline" className="text-xs">
                          Écart: {gap.gap || gap.gapValue}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-slate-500">Actuel:</span>
                        <span className="font-medium text-red-600">{gap.currentLevel}/5</span>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <span className="text-slate-500">Requis:</span>
                        <span className="font-medium text-green-600">{gap.requiredLevel}/5</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Development Plans */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-indigo-500" />
              Mes plans de développement
            </CardTitle>
            <CardDescription>
              Parcours de formation personnalisés pour développer vos compétences
            </CardDescription>
          </CardHeader>
          <CardContent>
            {developmentPlans.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <BookOpen className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p>Aucun plan de développement actif</p>
                <p className="text-sm mt-1">Votre manager vous assignera des plans de développement</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Active Plans */}
                {activePlans.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-3">En cours ({activePlans.length})</p>
                    <div className="grid gap-3">
                      {activePlans.map((plan, idx) => (
                        <div key={idx} className="p-4 rounded-lg border border-indigo-200 bg-indigo-50">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-slate-900">{plan.title || plan.skillName || plan.name}</p>
                              <p className="text-sm text-slate-500 mt-1">{plan.description}</p>
                              {plan.skillName && (
                                <Badge variant="outline" className="mt-2 text-xs">
                                  Compétence: {plan.skillName}
                                </Badge>
                              )}
                            </div>
                            <Badge className="bg-indigo-100 text-indigo-700">En cours</Badge>
                          </div>
                          
                          {/* Actions à effectuer */}
                          {plan.actions && plan.actions.length > 0 && (
                            <div className="mt-4 space-y-2">
                              <p className="text-xs font-medium text-slate-500 uppercase">Actions à effectuer :</p>
                              {plan.actions.map((action, i) => (
                                <div key={i} className="flex items-start gap-2 p-2 bg-white rounded border border-indigo-100">
                                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                                    action.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'
                                  }`}>
                                    {action.status === 'completed' ? '✓' : (i + 1)}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className={`text-sm font-medium ${action.status === 'completed' ? 'line-through text-slate-400' : 'text-slate-900'}`}>
                                      {action.title}
                                    </p>
                                    {action.description && (
                                      <p className="text-xs text-slate-500 mt-0.5">{action.description}</p>
                                    )}
                                    {action.deadline && (
                                      <p className="text-xs text-slate-400 mt-1">
                                        Échéance: {new Date(action.deadline).toLocaleDateString('fr-FR')}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {plan.progress !== undefined && (
                            <div className="mt-3">
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-slate-500">Progression</span>
                                <span className="font-medium">{plan.progress}%</span>
                              </div>
                              <Progress value={plan.progress} className="h-2" />
                            </div>
                          )}
                          
                          {plan.deadline && (
                            <p className="text-xs text-slate-500 mt-2">
                              Objectif: terminer avant le {new Date(plan.deadline).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Completed Plans */}
                {completedPlans.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-500 mb-3">Terminés ({completedPlans.length})</p>
                    <div className="grid gap-2">
                      {completedPlans.map((plan, idx) => (
                        <div key={idx} className="p-3 rounded-lg border border-green-200 bg-green-50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            <p className="font-medium text-slate-900">{plan.title || plan.name}</p>
                          </div>
                          <Badge className="bg-green-100 text-green-700">Terminé</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips Section */}
        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Conseil de développement</h3>
                <p className="text-sm text-slate-600">
                  {gapAnalysis?.readinessScore >= 80 
                    ? "Excellent niveau ! Pensez à partager vos connaissances avec vos collègues ou à explorer de nouvelles compétences complémentaires."
                    : gapAnalysis?.readinessScore >= 60 
                      ? "Bon travail ! Concentrez-vous sur les compétences avec le plus grand écart pour progresser efficacement."
                      : "Commencez par les compétences fondamentales de votre poste. Des petites améliorations régulières mènent à de grands résultats !"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
